import React from 'react'
import {Box, Typography} from '@mui/material'
import {ColumnDef} from 'src/components-new/data/DataTable'
import InitialsAvatar from 'src/components-new/primitives/InitialsAvatar'

export interface CustomerUserRow {
  _id: string
  title?: string
  firstName?: string
  lastName?: string
  authUserId?: string
  sex?: string
  nationality?: string
  phoneNumber?: string
  cellPhoneNumber?: string
  faxNumber?: string
  email?: string
  skypeId?: string
  preferredContacts?: string[]
  address?: {
    address?: string
    region?: string
    province?: string
    city?: string
    country?: string
    postalCode?: string
    streetNumber?: string
    locality?: string
  }
  [key: string]: any
}

interface CustomerUserColumnConfig {
  id: string
  label: string
}

function renderCell(id: string) {
  return (row: CustomerUserRow): React.ReactNode => {
    const value = (row as any)[id]
    return <Typography variant="body2">{value ?? '—'}</Typography>
  }
}

// An identity part (title/lastName/firstName) only shows if the PSP explicitly configured
// its id — except when the PSP hasn't configured this section at all (empty/missing
// columnOptions, e.g. local mock data), in which case every part falls back to visible so
// an unconfigured PSP still gets a usable identity column instead of a blank one.
function shouldIncludeIdentityPart(id: string, configuredIds: Set<string>): boolean {
  return configuredIds.size === 0 || configuredIds.has(id)
}

function getInitials(row: CustomerUserRow, configuredIds: Set<string>): string {
  const firstInitial = shouldIncludeIdentityPart('firstName', configuredIds) ? row.firstName?.[0] : undefined
  const lastInitial = shouldIncludeIdentityPart('lastName', configuredIds) ? row.lastName?.[0] : undefined
  const combined = `${firstInitial ?? ''}${lastInitial ?? ''}`
  return (combined || row.email?.[0] || '?').toUpperCase()
}

function getDisplayName(row: CustomerUserRow, configuredIds: Set<string>): string {
  const parts = [
    shouldIncludeIdentityPart('title', configuredIds) ? row.title : undefined,
    shouldIncludeIdentityPart('lastName', configuredIds) ? row.lastName : undefined,
    shouldIncludeIdentityPart('firstName', configuredIds) ? row.firstName : undefined,
  ].filter(Boolean)
  return parts.join(' ') || row.email || '—'
}

// title/lastName/firstName are merged into a single identity column (avatar + name, same
// pattern as the "Paziente" column in PatientsListNew.tsx) rather than three separate ones
// — it's the identifying column and always comes first. Merged ≠ unconditional though: each
// part still only renders if the PSP configured it (see shouldIncludeIdentityPart above). It
// also doubles as the mobile card header.
const IDENTITY_IDS = new Set(['title', 'lastName', 'firstName'])

// The merged column has no single PSP-configured id of its own, so its header borrows one
// of the labels the PSP already configured for the ids it absorbs — preferring cognome,
// then nome, then titolo — falling back to "Cliente" only when the PSP configures none of
// the three (e.g. local mock data).
function getIdentityColumnHeader(columnConfig?: CustomerUserColumnConfig[]): string {
  const labelById = new Map((columnConfig ?? []).map(col => [col.id, col.label]))
  return labelById.get('lastName') ?? labelById.get('firstName') ?? labelById.get('title') ?? 'Cliente'
}

function buildIdentityColumn(header: string, configuredIds: Set<string>): ColumnDef<CustomerUserRow> {
  return {
    key: 'cliente',
    header,
    mobileCardHeader: true,
    render: (row) => (
      <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
        <InitialsAvatar initials={getInitials(row, configuredIds)} />
        <Typography variant="body2" fontWeight={600}>{getDisplayName(row, configuredIds)}</Typography>
      </Box>
    ),
  }
}

/**
 * The identity column always comes first, headed with one of the PSP-configured labels
 * (see getIdentityColumnHeader). Any additional columns the PSP configures
 * (`customerUtilitiesConfigurations.columnOptions`) are appended after it, one plain-text
 * column per configured id — except title/lastName/firstName, which are dropped from that
 * list since they're already covered by the identity column and would otherwise be
 * duplicated.
 */
export function buildColumns(columnConfig?: CustomerUserColumnConfig[]): ColumnDef<CustomerUserRow>[] {
  const configuredIds = new Set((columnConfig ?? []).map(col => col.id))
  const extraColumns = (columnConfig ?? [])
    .filter(col => !IDENTITY_IDS.has(col.id))
    .map(col => ({
      key: col.id,
      header: col.label,
      render: renderCell(col.id),
    }))

  return [buildIdentityColumn(getIdentityColumnHeader(columnConfig), configuredIds), ...extraColumns]
}
